import React from 'react';
import { Helmet } from 'react-helmet-async';
import { organizationData } from '@/data/organizationSchema';
import { useI18n } from '@/contexts/I18nContext';

interface EnhancedOrganizationSchemaProps {
  overrideData?: Partial<typeof organizationData>;
}

const LOCALE_TO_BCP47: Record<string, string> = {
  es: 'es-ES',
  de: 'de-CH',
  fr: 'fr-CH',
  it: 'it-CH',
  pt: 'pt-PT',
  en: 'en-US',
};

/**
 * EnhancedOrganizationSchema - Comprehensive Organization JSON-LD
 *
 * Renders the full organization schema (logo, address, contactPoint, sameAs,
 * hasOfferCatalog, openingHours, geo, aggregateRating, reviews, awards,
 * payment, currencies, serviceArea, knowsAbout, slogan) with i18n awareness
 * via `inLanguage`. Designed to be mounted ONCE globally (in App.tsx) to avoid
 * JSON-LD duplication across pages.
 */
const EnhancedOrganizationSchema: React.FC<EnhancedOrganizationSchemaProps> = ({
  overrideData = {},
}) => {
  // Safe i18n read: this component is mounted inside I18nProvider in App.tsx,
  // but we guard for any edge case (tests / isolated renders).
  let locale = 'es';
  try {
    locale = useI18n().locale ?? 'es';
  } catch {
    locale = 'es';
  }

  const data = { ...organizationData, ...overrideData };

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${data.url}#organization`,
    name: data.name,
    legalName: data.legalName,
    url: data.url,
    logo: data.logo,
    image: data.logo,
    slogan: data.slogan,
    foundingDate: data.foundingDate,
    founders: data.founders,
    numberOfEmployees: data.numberOfEmployees,
    priceRange: data.priceRange,
    paymentAccepted: data.paymentAccepted,
    currenciesAccepted: data.currenciesAccepted,
    knowsAbout: data.knowsAbout,
    award: data.award,
    inLanguage: LOCALE_TO_BCP47[locale] ?? 'es-ES',
    address: {
      '@type': 'PostalAddress',
      ...data.address,
    },
    contactPoint: data.contactPoint,
    sameAs: data.sameAs,
    hasOfferCatalog: data.hasOfferCatalog,
    openingHoursSpecification: data.openingHoursSpecification,
    geo: {
      '@type': 'GeoCoordinates',
      ...data.geo,
    },
    serviceArea: data.serviceArea,
    aggregateRating: data.aggregateRating,
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default EnhancedOrganizationSchema;
